import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DEFAULT_LIST_PAGE_SIZE, PROCUREMENT_SEARCH_URL } from '../../constants';
import type { ProcurementSearchHit } from '../../domain/types';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from '../grids/GridPaginationBar';
import { FilterTiles } from '../master/FilterTiles';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

const COLUMNS: GroupedColumnDef[] = [
  { id: 'type', label: 'Type', header: 'Type' },
  { id: 'number', label: 'Number', header: 'Number' },
  { id: 'title', label: 'Title', header: 'Title' },
  { id: 'region', label: 'Region', header: 'Region' }
];

const TYPE_LABEL: Record<string, string> = {
  request: 'Request',
  purchaseOrder: 'Purchase order',
  rfq: 'Quote request',
  vendor: 'Vendor',
  well: 'Well'
};

function groupValue(row: ProcurementSearchHit, columnId: string): string {
  switch (columnId) {
    case 'type':
      return TYPE_LABEL[row.sourceType] ?? row.sourceType;
    case 'number':
      return row.number || '—';
    case 'title':
      return row.title || '—';
    case 'region':
      return row.region || '—';
    default:
      return '—';
  }
}

export function GlobalSearchBox(): ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const next = q.trim();
    navigate(next ? `/search?q=${encodeURIComponent(next)}` : '/search');
  }

  return (
    <form className="grid" onSubmit={onSubmit} data-testid="global-search-form">
      <label>
        <span className="sr-only">Search</span>
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search"
          aria-label="Search"
        />
      </label>
    </form>
  );
}

export function ProcurementSearchRegister({ authFetch }: { authFetch: AuthFetch }): ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const [typeFilter, setTypeFilter] = useState('all');
  const [rows, setRows] = useState<ProcurementSearchHit[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, request: 0, purchaseOrder: 0, rfq: 0, vendor: 0, well: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(1);
    setSelectedId(null);
  }, [q, typeFilter]);

  useEffect(() => {
    if (q.length < 2) {
      setRows([]);
      setTotal(0);
      setCounts({ all: 0, request: 0, purchaseOrder: 0, rfq: 0, vendor: 0, well: 0 });
      setError('');
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const source = typeFilter === 'all' ? '' : `&sourceTypes=${encodeURIComponent(typeFilter)}`;
      const listUrl = `${PROCUREMENT_SEARCH_URL}?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}${source}`;
      const countUrl = `${PROCUREMENT_SEARCH_URL}/counts?q=${encodeURIComponent(q)}`;
      try {
        const [listRes, countRes] = await Promise.all([authFetch(listUrl), authFetch(countUrl)]);
        if (cancelled) {
          return;
        }
        if (listRes.status === 400) {
          setRows([]);
          setTotal(0);
          setError('');
          return;
        }
        if (!listRes.ok) {
          setError(listRes.status === 403 ? 'You do not have permission to search.' : 'Search failed.');
          setRows([]);
          return;
        }
        const body = (await listRes.json()) as { items?: ProcurementSearchHit[]; totalCount?: number };
        setRows(Array.isArray(body.items) ? body.items : []);
        setTotal(body.totalCount ?? 0);
        setError('');
        if (countRes.ok) {
          const c = (await countRes.json()) as typeof counts;
          setCounts(c);
        }
      } catch {
        if (!cancelled) {
          setError('Search failed.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, q, typeFilter, page, pageSize]);

  const tiles = useMemo(
    () => [
      { id: 'all', label: 'All', count: counts.all },
      { id: 'request', label: 'Requests', count: counts.request },
      { id: 'purchaseOrder', label: 'Purchase orders', count: counts.purchaseOrder },
      { id: 'rfq', label: 'Quote requests', count: counts.rfq },
      { id: 'vendor', label: 'Vendors', count: counts.vendor },
      { id: 'well', label: 'Wells', count: counts.well }
    ],
    [counts]
  );

  const selected = rows.find((row) => `${row.sourceType}:${row.sourceId}` === selectedId) ?? null;

  function renderRow(row: ProcurementSearchHit): ReactElement {
    const id = `${row.sourceType}:${row.sourceId}`;
    return (
      <tr
        className={selectedId === id ? 'selected' : undefined}
        data-testid="search-row"
        onClick={() => setSelectedId(id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          navigate(row.href);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedId(id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td>{TYPE_LABEL[row.sourceType] ?? row.sourceType}</td>
        <td className="mono-id">{row.number}</td>
        <td>{row.title}</td>
        <td className="mono-id">{row.region || '—'}</td>
      </tr>
    );
  }

  return (
    <div className={`requests-shell${selected ? ' has-panel' : ''}`}>
      <FilterTiles
        testId="search-tiles"
        value={typeFilter}
        onChange={setTypeFilter}
        options={tiles}
      />
      <section className="card requests-grid-card" data-testid="search-grid">
        <div className="row spread requests-grid-toolbar">
          <h2>Search</h2>
        </div>
        <p className="hint">
          {q.length < 2
            ? 'Type a material, vendor, well, or document number.'
            : 'Click a row for the side panel; double-click to open the full page.'}
        </p>
        {loading && <p className="hint">Searching…</p>}
        {error && <p className="error">{error}</p>}
        <GroupedDataGrid<ProcurementSearchHit>
          columns={COLUMNS}
          rows={rows}
          getRowId={(r) => `${r.sourceType}:${r.sourceId}`}
          getGroupValue={groupValue}
          renderRow={renderRow}
          colSpan={4}
          emptyMessage="No records match this search."
        />
        <GridPaginationBar
          idPrefix="search-grid"
          page={page}
          pageSize={pageSize}
          totalCount={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </section>
      {selected && (
        <aside className="side-panel" aria-label="Search details" data-testid="search-side-panel">
          <div className="side-panel-header">
            <div className="side-panel-title-row">
              <h3>{selected.number}</h3>
            </div>
            <div className="side-panel-actions">
              <button type="button" className="secondary" onClick={() => navigate(selected.href)}>
                Open full page
              </button>
              <button type="button" className="icon-close" onClick={() => setSelectedId(null)} aria-label="Close panel">
                ×
              </button>
            </div>
          </div>
          <div className="side-panel-body">
            <dl className="detail-list">
              <dt>Type</dt>
              <dd>{TYPE_LABEL[selected.sourceType] ?? selected.sourceType}</dd>
              <dt>Number</dt>
              <dd className="mono-id">{selected.number}</dd>
              <dt>Region</dt>
              <dd className="mono-id">{selected.region || '—'}</dd>
              <dt>Snippet</dt>
              <dd>{selected.snippet || '—'}</dd>
            </dl>
          </div>
        </aside>
      )}
    </div>
  );
}
